import { VehicleState } from '@shared/vehicleSlice';
import { Journey as RealTimeJourney } from '../models/real-time/journey';
import { Call as RealTimeCall } from '../models/real-time/call';
import { Journey } from '../models/journey';
import { Call } from '../models/call';
import { DeepReadonly } from 'ts-essentials';
import {
  JourneyProgressStatus,
  JourneyServiceType,
} from '../models/protobuf/Journey';

function getCurrentStopIndex(
  indexInJourney: number | undefined,
  journeyProgressStatus: JourneyProgressStatus | undefined,
  numberOfStops: number | undefined,
): number | undefined {
  switch (journeyProgressStatus) {
    case JourneyProgressStatus.JOURNEY_PROGRESS_WAITING_FOR_JOURNEY_START:
    case JourneyProgressStatus.JOURNEY_PROGRESS_AT_FIRST_STOP:
    case JourneyProgressStatus.JOURNEY_PROGRESS_REACHED_STOP:
    case JourneyProgressStatus.JOURNEY_PROGRESS_ARRIVED_STOP:
      return indexInJourney;

    case JourneyProgressStatus.JOURNEY_PROGRESS_BETWEEN_STOPS:
    case JourneyProgressStatus.JOURNEY_PROGRESS_APPROACHING_STOP:
    case JourneyProgressStatus.JOURNEY_PROGRESS_LEAVING_STOP:
      return indexInJourney === undefined ||
        numberOfStops === undefined ||
        indexInJourney + 1 >= numberOfStops
        ? undefined
        : indexInJourney + 1;

    case JourneyProgressStatus.JOURNEY_PROGRESS_UNASSIGNED:
    case JourneyProgressStatus.JOURNEY_PROGRESS_OFF_ROUTE:
    case JourneyProgressStatus.UNRECOGNIZED:
    case undefined:
      return undefined;
  }
}

function getCurrentCall(
  currentStopIndex: number,
  calls: DeepReadonly<RealTimeCall[]> | undefined,
): { index: number; sequenceNumber: number } | undefined {
  const currentSequenceNumber = currentStopIndex + 1;
  if (calls === undefined) {
    return {
      index: currentStopIndex,
      sequenceNumber: currentSequenceNumber,
    };
  }
  const firstCallSequenceNumber = calls[0]?.sequenceNumber;
  if (
    firstCallSequenceNumber === undefined ||
    currentSequenceNumber < firstCallSequenceNumber
  ) {
    return undefined;
  }
  let currentCallIndex: number | undefined;
  const currentCall = calls.find((call, callIndex) => {
    if (call.sequenceNumber >= currentSequenceNumber && !call.isCancelled) {
      currentCallIndex = callIndex;
      return true;
    }
    return false;
  });
  return currentCallIndex !== undefined
    ? {
        index: currentCallIndex,
        sequenceNumber: currentCall!.sequenceNumber,
      }
    : undefined;
}

function getIsMovingTowardsNextStop(
  currentStopIndex: number,
  calls: DeepReadonly<RealTimeCall[]> | undefined,
  journeyProgressStatus: JourneyProgressStatus | undefined,
): boolean | undefined {
  const currentSequenceNumber = currentStopIndex + 1;
  const isMovingTowardsNonPublicStop =
    calls !== undefined &&
    calls.findIndex((call) => call.sequenceNumber === currentSequenceNumber) ===
      -1;
  if (isMovingTowardsNonPublicStop) {
    return true;
  }
  switch (journeyProgressStatus) {
    case JourneyProgressStatus.JOURNEY_PROGRESS_WAITING_FOR_JOURNEY_START:
    case JourneyProgressStatus.JOURNEY_PROGRESS_AT_FIRST_STOP:
    case JourneyProgressStatus.JOURNEY_PROGRESS_ARRIVED_STOP:
      return false;

    case JourneyProgressStatus.JOURNEY_PROGRESS_BETWEEN_STOPS:
    case JourneyProgressStatus.JOURNEY_PROGRESS_APPROACHING_STOP:
    case JourneyProgressStatus.JOURNEY_PROGRESS_REACHED_STOP:
    case JourneyProgressStatus.JOURNEY_PROGRESS_LEAVING_STOP:
      return true;

    case JourneyProgressStatus.JOURNEY_PROGRESS_UNASSIGNED:
    case JourneyProgressStatus.JOURNEY_PROGRESS_OFF_ROUTE:
    case JourneyProgressStatus.UNRECOGNIZED:
    case undefined:
      return undefined;
  }
}

function createCalls(
  vehicleState: DeepReadonly<VehicleState>,
  realTimeJourney: DeepReadonly<RealTimeJourney> | undefined,
  currentStopIndex: number,
  isMovingTowardsNextStop: boolean,
  hasRealTimeError: boolean,
): Call[] | undefined {
  if (realTimeJourney === undefined) {
    return vehicleState.stops?.map<Call>((stop, stopIndex) => {
      if (stopIndex < currentStopIndex) {
        return {
          type: 'passed',
          stopAreaName: stop,
          stopAreaShortName: undefined,
        };
      }
      return {
        type: 'basic',
        stopAreaName: stop,
        stopAreaShortName: undefined,
      };
    });
  }

  return realTimeJourney.calls.map((call) =>
    createCall(
      call,
      vehicleState,
      currentStopIndex,
      isMovingTowardsNextStop,
      hasRealTimeError,
    ),
  );
}

function createCall(
  call: RealTimeCall,
  vehicleState: DeepReadonly<VehicleState>,
  currentStopIndex: number,
  isMovingTowardsNextStop: boolean,
  hasRealTimeError: boolean,
): Call {
  const stopIndexForCall = call.sequenceNumber - 1;
  const stopAreaName = vehicleState.stops?.[stopIndexForCall];
  const isAlightingAllowed =
    stopIndexForCall === currentStopIndex && !isMovingTowardsNextStop
      ? undefined
      : call.isAlightingAllowed;

  if (hasRealTimeError) {
    return {
      type: 'basic',
      stopAreaName,
      stopAreaShortName: undefined,
      isAlightingAllowed,
    };
  }

  const stopAreaShortName = call.stopAreaShortName;

  if (
    currentStopIndex === stopIndexForCall &&
    call.departureTime !== undefined &&
    (vehicleState.journeyProgressStatus ===
      JourneyProgressStatus.JOURNEY_PROGRESS_WAITING_FOR_JOURNEY_START ||
      vehicleState.journeyProgressStatus ===
        JourneyProgressStatus.JOURNEY_PROGRESS_AT_FIRST_STOP ||
      vehicleState.journeyProgressStatus ===
        JourneyProgressStatus.JOURNEY_PROGRESS_ARRIVED_STOP)
  ) {
    return {
      type: 'awaiting-departure',
      stopAreaName,
      stopAreaShortName,
      departureTime: call.departureTime,
    };
  }

  if (currentStopIndex > stopIndexForCall) {
    return {
      type: 'passed',
      stopAreaName,
      stopAreaShortName,
      departedTime: call.departureTime,
    };
  }

  if (call.isCancelled) {
    return {
      type: 'cancelled',
      stopAreaName,
      stopAreaShortName,
      arrivalTime: call.estimatedArrivalTime,
    };
  }

  if (
    call.estimatedArrivalTime !== undefined &&
    (stopIndexForCall > currentStopIndex ||
      (stopIndexForCall === currentStopIndex && isMovingTowardsNextStop))
  ) {
    if (
      call.targetArrivalTime !== undefined &&
      call.estimatedArrivalTime > call.targetArrivalTime
    ) {
      return {
        type: 'delayed',
        stopAreaName,
        stopAreaShortName,
        arrivalTime: call.estimatedArrivalTime,
        targetArrivalTime: call.targetArrivalTime,
        isAlightingAllowed,
      };
    } else {
      return {
        type: 'upcoming',
        stopAreaName,
        stopAreaShortName,
        arrivalTime: call.estimatedArrivalTime,
        isAlightingAllowed,
      };
    }
  }

  return {
    type: 'basic',
    stopAreaName,
    stopAreaShortName,
    isAlightingAllowed,
  };
}

export default function createJourney(
  vehicleState: DeepReadonly<VehicleState>,
  realTimeJourney: DeepReadonly<RealTimeJourney> | undefined,
  hasRealTimeError: boolean,
): Journey | undefined {
  if (
    vehicleState.journeyServiceType === JourneyServiceType.JST_DEADRUN ||
    vehicleState.journeyGid !== vehicleState.journeyProgressJourneyGid
  ) {
    return undefined;
  }

  const currentStopIndex = getCurrentStopIndex(
    vehicleState.indexInJourney,
    vehicleState.journeyProgressStatus,
    vehicleState.stops?.length,
  );

  if (currentStopIndex === undefined) {
    return undefined;
  }

  const currentCall = getCurrentCall(currentStopIndex, realTimeJourney?.calls);

  if (currentCall === undefined) {
    return undefined;
  }

  const isMovingTowardsNextStop = getIsMovingTowardsNextStop(
    currentStopIndex,
    realTimeJourney?.calls,
    vehicleState.journeyProgressStatus,
  );

  if (isMovingTowardsNextStop === undefined) {
    return undefined;
  }

  const calls = createCalls(
    vehicleState,
    realTimeJourney,
    currentStopIndex,
    isMovingTowardsNextStop,
    hasRealTimeError,
  );

  if (calls === undefined || calls.length === 0) {
    return undefined;
  }

  return {
    calls,
    currentCallIndex: currentCall.index,
    currentCallSequenceNumber: currentCall.sequenceNumber,
    isMovingTowardsNextStop,
    continuesAs: realTimeJourney?.continuesAs,
  };
}
